import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";

interface ResourceConfigurationSectionProps {
  formData: {
    type: string;
    category: string;
    duration: number;
    author: string;
    source: string;
    color: string;
  };
  errors: { [key: string]: string };
  onChange: (field: string, value: any) => void;
  onErrorClear: (field: string) => void;
  onTypeChange: (newType: "article" | "video" | "audio" | "pdf" | "link") => void;
}

const ResourceConfigurationSection: React.FC<ResourceConfigurationSectionProps> = ({
  formData,
  errors,
  onChange,
  onErrorClear,
  onTypeChange,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant='h6'
        fontWeight='600'
        gutterBottom
        sx={{
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        ⚙️ Resource Configuration
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Resource Type</InputLabel>
            <Select
              label='Resource Type'
              value={formData.type}
              onChange={(e) => {
                onTypeChange(e.target.value as "article" | "video" | "audio" | "pdf" | "link");
              }}
              sx={{
                borderRadius: 2,
                minHeight: 64,
              }}
            >
              <MenuItem value='article'>📄 Article</MenuItem>
              <MenuItem value='video'>🎥 Video</MenuItem>
              <MenuItem value='audio'>🎵 Audio</MenuItem>
              <MenuItem value='pdf'>📋 PDF</MenuItem>
              <MenuItem value='link'>🔗 Link</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              label='Category'
              value={formData.category}
              onChange={(e) => onChange("category", e.target.value)}
              sx={{
                borderRadius: 2,
                minHeight: 64,
              }}
            >
              <MenuItem value='mindfulness'>🧘‍♀️ Mindfulness</MenuItem>
              <MenuItem value='stress-relief'>😌 Stress Relief</MenuItem>
              <MenuItem value='sleep'>😴 Sleep</MenuItem>
              <MenuItem value='anxiety'>😰 Anxiety</MenuItem>
              <MenuItem value='focus'>🎯 Focus</MenuItem>
              <MenuItem value='relationships'>💕 Relationships</MenuItem>
              <MenuItem value='general'>📚 General</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type='number'
            label='Duration (minutes)'
            placeholder='e.g., 15'
            value={formData.duration || ""}
            onChange={(e) => {
              const value = parseInt(e.target.value) || 0;
              onChange("duration", value);
              if (errors.duration) onErrorClear("duration");
            }}
            error={!!errors.duration}
            helperText={
              errors.duration ||
              (formData.type === "audio" || formData.type === "video"
                ? "Required: Duration of the audio/video content"
                : "How long will this resource take to consume?")
            }
            required={formData.type === "audio" || formData.type === "video"}
            inputProps={{
              min: 0,
              max: 300,
              step: 1,
            }}
            InputProps={{
              startAdornment: (
                <Typography sx={{ mr: 1, color: "text.secondary" }}>
                  ⏱️
                </Typography>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                minHeight: 64,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Author'
            placeholder='e.g., Dr. Jane Smith'
            value={formData.author}
            onChange={(e) => onChange("author", e.target.value)}
            InputProps={{
              startAdornment: (
                <Typography sx={{ mr: 1, color: "text.secondary" }}>
                  👤
                </Typography>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                minHeight: 64,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Source'
            placeholder='e.g., Wellness Institute'
            value={formData.source}
            onChange={(e) => onChange("source", e.target.value)}
            InputProps={{
              startAdornment: (
                <Typography sx={{ mr: 1, color: "text.secondary" }}>
                  🏢
                </Typography>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                minHeight: 64,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Color'
            placeholder='#667eea'
            value={formData.color}
            onChange={(e) => onChange("color", e.target.value)}
            helperText='Hex color code for the resource theme'
            InputProps={{
              startAdornment: (
                <Typography sx={{ mr: 1, color: "text.secondary" }}>
                  🎨
                </Typography>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                minHeight: 64,
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResourceConfigurationSection;

